import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Ticket, Copy, Check, Sparkles, BadgePercent } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Premium coupon banner shown on the Hero, just below the CTA row.
 * - Data is loaded from /api/public/promo-banner so Super Admin can toggle
 *   the headline, code, sub-text and enabled flag from the dashboard.
 * - Animations: ticket float-bob, shimmer sweep across the code chip every 3s,
 *   pulse halo around the % icon, sparkle pings on hover, copy-to-clipboard with confirm.
 */
export default function HeroCouponPromo() {
  const [promo, setPromo] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/promo-banner`, { timeout: 5000 });
        if (alive && res.data?.enabled) setPromo(res.data);
      } catch {
        /* silently hide on error — banner is optional */
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!promo) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(promo.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'promo_code_copy', promo_code: promo.code });
      } catch { /* ignore */ }
    } catch { /* clipboard blocked */ }
  };

  return (
    <div
      className="mt-5 sm:mt-6 max-w-xl mx-auto lg:mx-0 relative group"
      data-testid="hero-coupon-promo"
    >
      {/* Animated outer glow */}
      <div
        aria-hidden
        className="absolute -inset-0.5 rounded-2xl bg-gradient-to-r from-amber-400/40 via-emerald-400/40 to-amber-400/40 blur-md opacity-70 animate-coupon-glow"
        style={{ animation: 'couponGlow 3s ease-in-out infinite alternate' }}
      />

      {/* The ticket — dashed border + notches */}
      <div className="relative rounded-2xl bg-gradient-to-br from-amber-50 via-white to-emerald-50 ring-1 ring-amber-300/60 shadow-xl overflow-hidden">
        {/* Notch cut-outs (left & right) for a real "ticket" look */}
        <span aria-hidden className="absolute left-[55%] top-0 -translate-x-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-brand-blue-dark hidden sm:block" />
        <span aria-hidden className="absolute left-[55%] bottom-0 -translate-x-1/2 translate-y-1/2 w-5 h-5 rounded-full bg-brand-blue-dark hidden sm:block" />

        <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4">
          {/* Left: animated badge icon */}
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-amber-400/40 blur-xl animate-pulse" />
            <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg ring-2 ring-white/80" style={{ animation: 'couponBob 3.5s ease-in-out infinite' }}>
              <BadgePercent size={28} className="text-white drop-shadow" strokeWidth={2.5} />
              <Sparkles size={12} className="absolute -top-1 -right-1 text-amber-200 animate-ping" />
            </div>
          </div>

          {/* Middle: headline + subline */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Ticket size={12} className="text-amber-600" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-amber-700">{promo.badge_text || 'Limited Offer'}</span>
            </div>
            <div className="font-extrabold leading-tight text-slate-900 text-base sm:text-lg" data-testid="hero-coupon-headline">
              {promo.headline}
            </div>
            <div className="text-[11px] sm:text-xs text-slate-600 leading-snug mt-0.5" data-testid="hero-coupon-subline">
              {promo.subline}
            </div>
          </div>

          {/* Right: copy chip */}
          <button
            type="button"
            onClick={copy}
            aria-label={`Copy coupon code ${promo.code}`}
            data-testid="hero-coupon-copy-btn"
            className="relative flex-shrink-0 group/code"
          >
            <div className="relative px-2.5 sm:px-3 py-2 sm:py-2.5 rounded-xl border-2 border-dashed border-amber-500 bg-white text-emerald-700 font-mono font-bold text-sm sm:text-base shadow-inner overflow-hidden">
              {/* Shimmer sweep */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/80 to-transparent"
                style={{ animation: 'couponShimmer 3s ease-in-out infinite' }}
              />
              <span className="relative flex items-center gap-1.5">
                {copied ? (
                  <>
                    <Check size={14} className="text-emerald-600" />
                    <span className="text-emerald-700">Copied!</span>
                  </>
                ) : (
                  <>
                    <span data-testid="hero-coupon-code">{promo.code}</span>
                    <Copy size={12} className="text-slate-500 group-hover/code:text-emerald-600 transition" />
                  </>
                )}
              </span>
            </div>
            <span className="block text-[9px] sm:text-[10px] text-amber-700 font-semibold mt-1 text-center">Tap to Copy</span>
          </button>
        </div>

        {/* Bottom terms strip */}
        {promo.terms && (
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-3 py-1.5 text-[10px] sm:text-[11px] font-medium tracking-wide text-center" data-testid="hero-coupon-terms">
            {promo.terms}
          </div>
        )}
      </div>

      {/* Keyframes injected once — Tailwind doesn't ship these */}
      <style>{`
        @keyframes couponGlow { 0%   { opacity: .35; transform: scale(1); }
                                100% { opacity: .85; transform: scale(1.015); } }
        @keyframes couponBob  { 0%, 100% { transform: translateY(0) rotate(-2deg); }
                                50%      { transform: translateY(-3px) rotate(2deg); } }
        @keyframes couponShimmer { 0%   { transform: translateX(-100%); }
                                   60%, 100% { transform: translateX(200%); } }
      `}</style>
    </div>
  );
}
